import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeftIcon,
  RssIcon,
  MagnifyingGlassIcon,
  CheckCircleIcon,
  ArrowPathIcon,
  BuildingLibraryIcon,
  GlobeAltIcon,
  NewspaperIcon,
  MapPinIcon,
} from '@heroicons/react/24/outline';
import { Card, CardContent } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { cn } from '@/utils/cn';
import threatIntelligenceService, { type FeedPreference } from '@/services/threat-intelligence.service';

interface LocationState {
  fromTab?: 'reported' | 'feed' | 'reports';
}

type CategoryKey = 'regional' | 'national_security' | 'breaking_news';

interface GroupedFeeds {
  regional: FeedPreference[];
  national_security: FeedPreference[];
  breaking_news: FeedPreference[];
}

const categoryInfo: Record<CategoryKey, { label: string; icon: React.ComponentType<{ className?: string }>; description: string }> = {
  regional: {
    label: 'Regional (Minneapolis/Minnesota)',
    icon: MapPinIcon,
    description: 'Local news sources covering the Twin Cities and Minnesota area',
  },
  national_security: {
    label: 'National Security & Intelligence',
    icon: BuildingLibraryIcon,
    description: 'Government agencies and security-focused intelligence sources',
  },
  breaking_news: {
    label: 'National Breaking News',
    icon: NewspaperIcon,
    description: 'Major national news networks for breaking stories',
  },
};

const reliabilityLabels: Record<string, string> = {
  A: 'Highly Reliable',
  B: 'Usually Reliable',
  C: 'Fairly Reliable',
  D: 'Not Usually Reliable',
  E: 'Unreliable',
  F: 'Unknown',
};

const reliabilityColors: Record<string, string> = {
  A: 'bg-success-100 text-success-800 dark:bg-success-900/20 dark:text-success-300',
  B: 'bg-primary-100 text-primary-800 dark:bg-primary-900/20 dark:text-primary-300',
  C: 'bg-warning-100 text-warning-800 dark:bg-warning-900/20 dark:text-warning-300',
  D: 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-300',
  E: 'bg-danger-100 text-danger-800 dark:bg-danger-900/20 dark:text-danger-300',
  F: 'bg-secondary-100 text-secondary-800 dark:bg-secondary-800 dark:text-secondary-300',
};

const typeColors: Record<string, string> = {
  government: 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300',
  open_source: 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-300',
};

export default function ManageFeeds() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const state = location.state as LocationState | null;
  const originTab = state?.fromTab ?? 'feed';

  const [feeds, setFeeds] = useState<FeedPreference[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [togglingIds, setTogglingIds] = useState<Set<string>>(new Set());
  const [expandedCategories, setExpandedCategories] = useState<Set<CategoryKey>>(
    new Set(['regional', 'national_security', 'breaking_news'])
  );

  const fetchFeeds = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await threatIntelligenceService.getFeedPreferences();
      setFeeds(response);
    } catch (err) {
      console.error('Failed to fetch feed preferences:', err);
      setError('Failed to load feed preferences. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchFeeds();
  }, [fetchFeeds]);

  const handleBack = () => {
    navigate('/security/threats', { state: { activeTab: originTab } });
  };

  const handleToggle = async (sourceId: string, currentEnabled: boolean) => {
    setTogglingIds((prev) => new Set(prev).add(sourceId));
    try {
      await threatIntelligenceService.toggleFeedPreference(sourceId, !currentEnabled);
      setFeeds((prev) =>
        prev.map((feed) =>
          feed.sourceId === sourceId ? { ...feed, enabled: !currentEnabled } : feed
        )
      );
      // Invalidate feed-items cache so Threats Feed tab refetches with new preferences
      void queryClient.invalidateQueries({ queryKey: ['feed-items'] });
    } catch (err) {
      console.error('Failed to toggle feed preference:', err);
      setError('Failed to update preference. Please try again.');
    } finally {
      setTogglingIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(sourceId);
        return newSet;
      });
    }
  };

  const handleEnableAll = async (category: CategoryKey) => {
    const categoryFeeds = groupedFeeds[category].filter((f) => !f.enabled);
    if (categoryFeeds.length === 0) {
      return;
    }

    const preferences = categoryFeeds.map((f) => ({
      sourceId: f.sourceId,
      enabled: true,
    }));

    try {
      await threatIntelligenceService.bulkUpdateFeedPreferences(preferences);
      setFeeds((prev) =>
        prev.map((feed) =>
          feed.category === category ? { ...feed, enabled: true } : feed
        )
      );
      // Invalidate feed-items cache so Threats Feed tab refetches with new preferences
      void queryClient.invalidateQueries({ queryKey: ['feed-items'] });
    } catch (err) {
      console.error('Failed to enable all feeds:', err);
      setError('Failed to update preferences. Please try again.');
    }
  };

  const handleDisableAll = async (category: CategoryKey) => {
    const categoryFeeds = groupedFeeds[category].filter((f) => f.enabled);
    if (categoryFeeds.length === 0) {
      return;
    }

    const preferences = categoryFeeds.map((f) => ({
      sourceId: f.sourceId,
      enabled: false,
    }));

    try {
      await threatIntelligenceService.bulkUpdateFeedPreferences(preferences);
      setFeeds((prev) =>
        prev.map((feed) =>
          feed.category === category ? { ...feed, enabled: false } : feed
        )
      );
      // Invalidate feed-items cache so Threats Feed tab refetches with new preferences
      void queryClient.invalidateQueries({ queryKey: ['feed-items'] });
    } catch (err) {
      console.error('Failed to disable all feeds:', err);
      setError('Failed to update preferences. Please try again.');
    }
  };

  const toggleCategory = (category: CategoryKey) => {
    setExpandedCategories((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(category)) {
        newSet.delete(category);
      } else {
        newSet.add(category);
      }
      return newSet;
    });
  };

  const filteredFeeds = feeds.filter(
    (feed) =>
      feed.sourceName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      feed.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const groupedFeeds: GroupedFeeds = {
    regional: filteredFeeds.filter((f) => f.category === 'regional'),
    national_security: filteredFeeds.filter((f) => f.category === 'national_security'),
    breaking_news: filteredFeeds.filter((f) => f.category === 'breaking_news'),
  };

  const formatLastSync = (dateString?: string) => {
    if (!dateString) {
      return 'Never synced';
    }
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));

    if (diffInMinutes < 1) {
      return 'Just now';
    }
    if (diffInMinutes < 60) {
      return `${diffInMinutes}m ago`;
    }
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) {
      return `${diffInHours}h ago`;
    }
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const enabledCount = feeds.filter((f) => f.enabled).length;
  const totalCount = feeds.length;

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4, ease: 'easeOut' },
    },
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="p-4 sm:p-6 space-y-6"
    >
      {/* Header */}
      <motion.div variants={itemVariants}>
        <div className="flex items-center space-x-4">
          <button
            onClick={handleBack}
            className="p-2 rounded-lg bg-secondary-100 dark:bg-secondary-800 hover:bg-secondary-200 dark:hover:bg-secondary-700 transition-colors"
            aria-label="Go back"
          >
            <ArrowLeftIcon className="w-5 h-5 text-secondary-600 dark:text-secondary-400" />
          </button>
          <div className="flex-1">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-primary-100 dark:bg-primary-900/20 rounded-lg">
                <RssIcon className="h-6 w-6 text-primary-600 dark:text-primary-400" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-secondary-900 dark:text-white">
                  Manage Intelligence Feeds
                </h1>
                <p className="text-sm text-secondary-500 dark:text-secondary-400 mt-0.5">
                  {enabledCount} of {totalCount} feeds enabled
                </p>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Main Content */}
      <motion.div variants={itemVariants}>
        <Card>
          <CardContent className="p-6">
            {/* Search */}
            <div className="mb-6">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-secondary-400" />
                <Input
                  type="text"
                  placeholder="Search feeds..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-4 p-3 bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800 rounded-lg text-danger-700 dark:text-danger-300 text-sm">
                {error}
              </div>
            )}

            {/* Loading State */}
            {isLoading ? (
              <div className="flex flex-col items-center justify-center py-12">
                <ArrowPathIcon className="w-8 h-8 text-primary-600 animate-spin mb-4" />
                <p className="text-secondary-600 dark:text-secondary-400">Loading feeds...</p>
              </div>
            ) : filteredFeeds.length === 0 ? (
              <div className="text-center py-12">
                <GlobeAltIcon className="w-12 h-12 text-secondary-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-secondary-900 dark:text-white mb-2">
                  No feeds found
                </h3>
                <p className="text-secondary-600 dark:text-secondary-400">
                  {searchQuery
                    ? 'Try adjusting your search query.'
                    : 'No pre-configured feeds available.'}
                </p>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Category Sections */}
                {(Object.keys(categoryInfo) as CategoryKey[]).map((category) => {
                  const categoryData = categoryInfo[category];
                  const CategoryIcon = categoryData.icon;
                  const categoryFeeds = groupedFeeds[category];
                  const isExpanded = expandedCategories.has(category);
                  const enabledInCategory = categoryFeeds.filter((f) => f.enabled).length;

                  if (categoryFeeds.length === 0) {
                    return null;
                  }

                  return (
                    <div key={category} className="border border-secondary-200 dark:border-secondary-700 rounded-lg overflow-hidden">
                      {/* Category Header */}
                      <button
                        onClick={() => toggleCategory(category)}
                        className="w-full flex items-center justify-between p-4 bg-secondary-50 dark:bg-secondary-800/50 hover:bg-secondary-100 dark:hover:bg-secondary-800 transition-colors text-left"
                      >
                        <div className="flex items-center space-x-3">
                          <div className="p-2 bg-primary-100 dark:bg-primary-900/20 rounded-lg">
                            <CategoryIcon className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-secondary-900 dark:text-white">
                              {categoryData.label}
                            </h3>
                            <p className="text-sm text-secondary-500 dark:text-secondary-400">
                              {enabledInCategory} of {categoryFeeds.length} enabled
                            </p>
                          </div>
                        </div>
                        <motion.div
                          animate={{ rotate: isExpanded ? 180 : 0 }}
                          transition={{ duration: 0.2 }}
                        >
                          <svg
                            className="w-5 h-5 text-secondary-500"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </motion.div>
                      </button>

                      {/* Category Content */}
                      <AnimatePresence>
                        {isExpanded && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                          >
                            {/* Bulk Actions */}
                            <div className="px-4 py-2 border-b border-secondary-200 dark:border-secondary-700 bg-white dark:bg-secondary-900 flex items-center justify-between text-sm">
                              <p className="text-secondary-600 dark:text-secondary-400">
                                {categoryData.description}
                              </p>
                              <div className="flex space-x-2">
                                <button
                                  onClick={() => handleEnableAll(category)}
                                  className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 font-medium"
                                >
                                  Enable All
                                </button>
                                <span className="text-secondary-300 dark:text-secondary-600">|</span>
                                <button
                                  onClick={() => handleDisableAll(category)}
                                  className="text-secondary-600 hover:text-secondary-700 dark:text-secondary-400 dark:hover:text-secondary-300 font-medium"
                                >
                                  Disable All
                                </button>
                              </div>
                            </div>

                            {/* Feed Items */}
                            <div className="divide-y divide-secondary-200 dark:divide-secondary-700">
                              {categoryFeeds.map((feed) => (
                                <div
                                  key={feed.sourceId}
                                  className="p-4 bg-white dark:bg-secondary-900 hover:bg-secondary-50 dark:hover:bg-secondary-800/30 transition-colors"
                                >
                                  <div className="flex items-start justify-between gap-4">
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-2 mb-1">
                                        <h4 className="font-medium text-secondary-900 dark:text-white truncate">
                                          {feed.sourceName}
                                        </h4>
                                        {feed.enabled && (
                                          <CheckCircleIcon className="w-4 h-4 text-success-500 flex-shrink-0" />
                                        )}
                                      </div>
                                      {feed.description && (
                                        <p className="text-sm text-secondary-600 dark:text-secondary-400 mb-2">
                                          {feed.description}
                                        </p>
                                      )}
                                      <div className="flex flex-wrap items-center gap-2">
                                        <span
                                          className={cn(
                                            'px-2 py-0.5 text-xs font-medium rounded-full',
                                            typeColors[feed.sourceType] || typeColors.open_source
                                          )}
                                        >
                                          {feed.sourceType === 'government' ? 'Government' : 'Open Source'}
                                        </span>
                                        <span
                                          className={cn(
                                            'px-2 py-0.5 text-xs font-medium rounded-full',
                                            reliabilityColors[feed.sourceReliability] || reliabilityColors.C
                                          )}
                                        >
                                          {feed.sourceReliability} - {reliabilityLabels[feed.sourceReliability] ?? 'Unknown'}
                                        </span>
                                        <span className="text-xs text-secondary-500 dark:text-secondary-400">
                                          Last sync: {formatLastSync(feed.lastSyncedAt ?? undefined)}
                                        </span>
                                      </div>
                                    </div>

                                    {/* Toggle Switch */}
                                    <div className="flex-shrink-0">
                                      <button
                                        onClick={() => handleToggle(feed.sourceId, feed.enabled)}
                                        disabled={togglingIds.has(feed.sourceId)}
                                        className={cn(
                                          'relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2',
                                          feed.enabled
                                            ? 'bg-primary-600'
                                            : 'bg-secondary-300 dark:bg-secondary-600',
                                          togglingIds.has(feed.sourceId) && 'opacity-50 cursor-wait'
                                        )}
                                      >
                                        <span
                                          className={cn(
                                            'inline-block h-4 w-4 transform rounded-full bg-white transition-transform',
                                            feed.enabled ? 'translate-x-6' : 'translate-x-1'
                                          )}
                                        />
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>

      {/* Footer */}
      <motion.div variants={itemVariants}>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-secondary-600 dark:text-secondary-400">
                Feeds sync automatically every 15 minutes
              </p>
              <button
                onClick={handleBack}
                className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors"
              >
                Done
              </button>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </motion.div>
  );
}
